import { Box, IconButton, Typography, Button, Stack, Grid2 as Grid, Checkbox, FormControlLabel } from '@mui/material';
import { useFieldArray, useWatch, Controller } from 'react-hook-form';
import { useEffect } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Select from '@/components/Common/Select';

interface OscarEmrFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const defaultProvider = () => ({
  provider_name: '',
  locations: [],
  accept_new_patient: false,
  services: [
    {
      appointment_type: '',
      appointment_mode: [],
      template_codes: []
    }
  ]
});

const OscarEmrForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues
}: OscarEmrFormProps) => {
  const providersField = configFields?.find((f: any) => f.key === 'providers');
  const otherFields = configFields?.filter((f: any) => f.key !== 'providers');

  const {
    fields: providers,
    append,
    remove
  } = useFieldArray({
    control,
    name: `${namePrefix}.providers`
  });

  useWatch({
    control,
    name: `${namePrefix}.providers`
  });

  const locations = useWatch({
    control,
    name: `${namePrefix}.locations`
  });

  const locationOptions = (locations || [])
    .filter((location: any) => location?.location_code)
    .map((location: any) => ({
      value: location.location_code,
      label: location.location_name || location.location_code
    }));

  useEffect(() => {
    const existing = getValues(`${namePrefix}.providers`);
    if (!existing || existing.length === 0) {
      append(defaultProvider());
    }
  }, [append, getValues, namePrefix]);

  return (
    <>
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      <Box>
        <Typography fontSize={16} fontWeight={600} my={2}>
          Provider Details
        </Typography>

        {providers.map((provider: any, index: number) => (
          <ProviderItem
            key={provider.id}
            index={index}
            control={control}
            remove={remove}
            disabled={disabled}
            namePrefix={namePrefix}
            providersFields={providersField}
            totalProviders={providers.length}
            locationOptions={locationOptions}
          />
        ))}

        <Box display='flex' justifyContent='center'>
          <Button variant='contained' disabled={disabled} onClick={() => append(defaultProvider())}>
            + Add Another Provider
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default OscarEmrForm;

const ProviderItem = ({
  index,
  control,
  remove,
  disabled,
  namePrefix,
  totalProviders,
  providersFields,
  locationOptions
}: any) => {
  const providerPrefix = `${namePrefix}.providers.${index}`;
  const providerField = providersFields?.group?.find((f: any) => f.key === 'provider_name');
  const locationsField = providersFields?.group?.find((f: any) => f.key === 'locations');
  const appointmentTypeField = providersFields?.group?.find((f: any) => f.key === 'appointment_type');
  const appointmentModeField = providersFields?.group?.find((f: any) => f.key === 'appointment_mode');
  const templateCodesField = providersFields?.group?.find((f: any) => f.key === 'template_codes');
  const providerOptions = providerField?.options || [];
  const appointmentTypeOptions = appointmentTypeField?.options || [];
  const appointmentModeOptions = appointmentModeField?.options || [];
  const templateCodeOptions = templateCodesField?.options || [];

  const {
    fields: appointmentTypes,
    append: addAppointmentType,
    remove: removeAppointmentType
  } = useFieldArray({
    control,
    name: `${providerPrefix}.services`
  });

  useEffect(() => {
    if (appointmentTypes.length === 0) {
      addAppointmentType({
        appointment_type: '',
        appointment_mode: [],
        template_codes: []
      });
    }
  }, [appointmentTypes.length, addAppointmentType]);

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='12px'>
      <Stack direction='row' justifyContent='space-between' mb={2}>
        <Typography fontWeight={600}>Provider #{index + 1}</Typography>
        {totalProviders > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Grid container spacing={5} alignItems='center'>
        <Grid size={9}>
          <Select
            label='Select Provider'
            name={`${providerPrefix}.provider_name`}
            control={control}
            disabled={disabled}
            options={providerOptions}
            fullWidth
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name={`${providerPrefix}.accept_new_patient`}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label='Accept New Patients'
              />
            )}
          />
        </Grid>
      </Grid>

      <Box mt={2}>
        <Select
          label={locationsField?.label || 'Locations'}
          name={`${providerPrefix}.locations`}
          control={control}
          disabled={disabled}
          options={locationOptions}
          multiple
          fullWidth
        />
      </Box>

      <Box mt={3}>
        <Box display='flex' justifyContent='space-between' mb={1}>
          <Typography fontWeight={500}>Appointment Types</Typography>
          <Button
            disabled={disabled}
            size='small'
            variant='contained'
            onClick={() =>
              addAppointmentType({
                appointment_type: '',
                appointment_mode: [],
                template_codes: []
              })
            }
          >
            Add Appointment Types
          </Button>
        </Box>

        {appointmentTypes.map((appointmentType: any, sIndex: number) => {
          const servicePrefix = `${providerPrefix}.services.${sIndex}`;
          return (
            <Box key={appointmentType.id} mb={2}>
              <Box display='flex' gap={2} alignItems='center'>
                <Select
                  label={appointmentTypeField?.label || 'Select Type'}
                  name={`${servicePrefix}.appointment_type`}
                  control={control}
                  disabled={disabled}
                  options={appointmentTypeOptions}
                  fullWidth
                />
                <Select
                  label={appointmentModeField?.label || 'Select Mode'}
                  name={`${servicePrefix}.appointment_mode`}
                  control={control}
                  disabled={disabled}
                  options={appointmentModeOptions}
                  multiple
                  fullWidth
                />
                {appointmentTypes.length > 1 && !disabled && (
                  <IconButton onClick={() => removeAppointmentType(sIndex)}>
                    <RiDeleteBin6Line />
                  </IconButton>
                )}
              </Box>

              <Box mt={1}>
                <Select
                  label='Template Codes'
                  name={`${servicePrefix}.template_codes`}
                  control={control}
                  options={templateCodeOptions}
                  multiple
                  disabled={disabled}
                  fullWidth
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
